import React, { useRef, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { ExternalLink, ArrowUpRight } from "lucide-react";
import { FaGithub as Github } from "react-icons/fa";

const CardContainer = styled(motion.div)`
  perspective: 1000px;
  width: 100%;
  max-width: 380px;
  min-height: 480px;
  display: flex;
`;

const CardInner = styled(motion.div)`
  width: 100%;
  position: relative;
  border-radius: 20px;
  background: ${({ theme }) => theme.card_light};
  border: 1px solid ${({ theme }) => theme.border};
  box-shadow: 0 15px 35px rgba(2, 6, 23, 0.35);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  transform-style: preserve-3d;
  cursor: pointer;

  &:hover {
    border-color: rgba(6, 182, 212, 0.5);
    box-shadow: 0 20px 40px -10px rgba(6, 182, 212, 0.2);
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: ${({ theme }) => theme.bgSoft};
`;

const ProjectImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);

  ${CardInner}:hover & {
    transform: scale(1.06);
  }
`;

const CategoryBadge = styled.span`
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 2;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #06b6d4;
  background: rgba(3, 7, 18, 0.85);
  border: 1px solid rgba(6, 182, 212, 0.35);
  backdrop-filter: blur(8px);
`;

const CardBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Title = styled.h3`
  font-size: 19px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
  margin-bottom: 8px;
  line-height: 1.35;
  transition: color 0.2s ease;

  ${CardInner}:hover & {
    color: #06b6d4;
  }
`;

const Description = styled.p`
  font-size: 13.5px;
  line-height: 1.6;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 16px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TagsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 18px;
`;

const Tag = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 999px;
  color: ${({ theme }) => theme.primary};
  background: ${({ theme }) => `${theme.primary}12`};
  border: 1px solid ${({ theme }) => `${theme.primary}2f`};
`;

const CardFooter = styled.div`
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.border};
`;

const ActionButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const ActionButton = styled.a`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;

  ${({ primary, theme }) =>
    primary
      ? `
    background: linear-gradient(135deg, #06b6d4 0%, #0284c7 100%);
    color: #030712;
    border: none;
    box-shadow: 0 4px 14px rgba(6, 182, 212, 0.35);

    &:hover {
      background: linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%);
      transform: translateY(-2px);
      box-shadow: 0 6px 18px rgba(6, 182, 212, 0.5);
    }
  `
      : `
    background: ${theme.bgSoft};
    color: ${theme.text_primary};
    border: 1px solid ${theme.border};

    &:hover {
      background: ${theme.card_hover};
      border-color: rgba(6, 182, 212, 0.4);
      color: #06b6d4;
      transform: translateY(-2px);
    }
  `}
`;

const ProjectCard3D = ({ project, onOpenModal }) => {
  const cardRef = useRef(null);
  const [rotate, setRotate] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -6;
    const rotateY = ((x - centerX) / centerX) * 6;

    setRotate({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    setRotate({ x: 0, y: 0 });
  };

  return (
    <CardContainer
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      initial={{ opacity: 0, y: 25 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4 }}
    >
      <CardInner
        animate={{
          rotateX: rotate.x,
          rotateY: rotate.y
        }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        onClick={() => onOpenModal(project)}
      >
        <ImageWrapper>
          <CategoryBadge>{project.category}</CategoryBadge>

          <ProjectImage
            src={project.image || project.fallbackImage || "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80"}
            alt={project.title}
            loading="lazy"
            onError={(e) => {
              if (project.fallbackImage && e.target.src !== project.fallbackImage) {
                e.target.src = project.fallbackImage;
              } else {
                e.target.src = "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80";
              }
            }}
          />
        </ImageWrapper>

        <CardBody>
          <Title>{project.title}</Title>
          <Description>{project.description}</Description>

          <TagsWrapper>
            {(project.technologies || project.tags || []).slice(0, 4).map((tech, idx) => (
              <Tag key={idx}>{tech}</Tag>
            ))}
            {(project.technologies || project.tags || []).length > 4 && (
              <Tag>+{(project.technologies || project.tags || []).length - 4}</Tag>
            )}
          </TagsWrapper>

          <CardFooter onClick={(e) => e.stopPropagation()}>
            <ActionButtonGroup>
              {project.webapp && (
                <ActionButton
                  primary={true}
                  href={project.webapp}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink size={14} />
                  Live Demo
                </ActionButton>
              )}
              {project.github ? (
                <ActionButton
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Github size={14} />
                  Code
                </ActionButton>
              ) : (
                <ActionButton
                  as="button"
                  onClick={() => onOpenModal(project)}
                >
                  <ArrowUpRight size={14} />
                  Details
                </ActionButton>
              )}
            </ActionButtonGroup>
          </CardFooter>
        </CardBody>
      </CardInner>
    </CardContainer>
  );
};

export default ProjectCard3D;
